import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Tag } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { DeleteFilled } from '@ant-design/icons';
import ErrorHelper from 'helpers/ErrorHelper';
import ShoppingCentersAPI from 'libs/api/admin/shoppingCenters';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import SsoUsersRoles from '../../../../../libs/api/ssoUsersRoles';
import styles from './main/style.module.scss';

const api = new SsoUsersRoles();
const apiPermissionGroups = new SsoUsersRoles('permission_groups');

const useUserRoles = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [premisionsGroups, setPremisionsGroups] = useState([]);

  const {
    data,
    singleItem: singleShop,
    loading,
    onChangeTableFilter,
    onDelete,
    updateInProcess,
    setUpdateInProcess,
    fetchList,
  } = useApi({
    resourceKey: 'user_roles',
    singleResourceKey: 'user_role',
    api,
    isMainPage: !id,
  });
  const handleCreateRole = useCallback(
    async (values, idFromTable) => {
      setUpdateInProcess(true);
      try {
        const data = {
          ...values,
        };
        data.permission_group_ids = [data.permission_group_ids];
        if (idFromTable) {
          await api.put(idFromTable, { data });
        } else {
          if (!id || id === 'create') {
            await api.create({ data });
            navigate('/sso/user_roles/page/role');
          } else {
            await api.put(id, { data });
            navigate('/sso/user_roles/page/role');
          }
        }
        await fetchList();
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, id],
  );
  const filters = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    return [
      {
        title: t('Nome'),
        dataIndex: 'title',
      },
      {
        title: t('Preset permessi'),
        dataIndex: 'permission_groups',
        render: (data, row) => {
          if (data.length <= 3) {
            return data.map((i) => {
              const onClose = (id) => {
                const permission_group_ids = data
                  .filter((j) => j.id !== id)
                  .map((j) => j.id);
                handleCreateRole({ permission_group_ids }, row.id);
              };
              return (
                <Tag
                  key={i.id}
                  className={styles.tag}
                  closable={row.can_destroy}
                  onClose={() => onClose(i.id)}
                >
                  {i.title}
                </Tag>
              );
            });
          } else {
            return (
              <>
                {data.slice(0, 3).map((i) => {
                  const onClose = (id) => {
                    const permission_group_ids = data
                      .filter((j) => j.id !== id)
                      .map((j) => j.id);
                    handleCreateRole({ permission_group_ids }, row.id);
                  };
                  return (
                    <Tag
                      key={i.id}
                      className={styles.tag}
                      closable={row.can_destroy}
                      onClose={() => onClose(i.id)}
                    >
                      {i.title}
                    </Tag>
                  );
                })}
                <Tag className={styles.tag}>+ {data.slice(3).length}</Tag>
              </>
            );
          }
        },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        render: (data, row) => (
          <Row gutter={16} justify={'end'}>
            {row.can_update && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteFilled />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [t, pathname]);

  useEffect(() => {
    if (id && singleShop) {
      try {
        let fieldsValues = [];
        for (let key in singleShop) {
          if (key === 'permission_groups') {
            fieldsValues.push({
              name: 'permission_group_ids',
              value: singleShop[key].map((i) => i.id),
            });
          } else if (key !== 'id' && key !== 'extrastock_map') {
            fieldsValues.push({
              name: key,
              value: singleShop[key],
            });
          }
        }
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    }
  }, [id, singleShop, form]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { permission_groups = [] },
        } = await apiPermissionGroups.list({
          params: {
            per: 1000,
          },
        });
        setPremisionsGroups(permission_groups);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  return {
    columns,
    data,
    singleShop,
    loading,
    updateInProcess,
    onChangeTableFilter,
    form,
    filters,
    premisionsGroups,
    handleCreateRole,
    onDelete,
  };
};

export default useUserRoles;
